import { Inject, Injectable } from '@angular/core';
import { PIPELINE_SETTINGS_CONFIG_TOKEN, PipelineSettingsConfig } from './tokens';
import { SalesPipelinesService, PipelineInterface, StageInterface } from '@vendasta/sales-opportunities';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export function DisplayPercentageToStored(display: number): number {
  return display / 100;
}

export function StoredPercentageToDisplay(stored: number): number {
  return stored * 100;
}

@Injectable()
export class PipelineSettingsService {
  private readonly partnerId$ = new Observable<string>();
  private readonly currentPipelineId$$ = new BehaviorSubject<string>('');
  private readonly pipelineUpdateEvents$$ = new BehaviorSubject<string>('');
  readonly pipelines$: Observable<Map<string, PipelineInterface>> = new Observable<Map<string, PipelineInterface>>();
  readonly currentPipeline$: Observable<PipelineInterface> = new Observable<PipelineInterface>();
  private readonly defaultPipelineName = this.translateService.instant(
    'FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.NAME',
  );
  private readonly defaultPipelineStages: StageInterface[] = [
    {
      name: this.translateService.instant('FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.STAGE_ONE'),
      probability: 0.1,
    },
    {
      name: this.translateService.instant('FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.STAGE_TWO'),
      probability: 0.3,
    },
    {
      name: this.translateService.instant('FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.STAGE_THREE'),
      probability: 0.99,
    },
  ];

  constructor(
    @Inject(PIPELINE_SETTINGS_CONFIG_TOKEN) private readonly config: PipelineSettingsConfig,
    private readonly salesPipelinesService: SalesPipelinesService,
    private translateService: TranslateService,
  ) {
    this.partnerId$ = this.config.partnerId$;
    this.pipelines$ = combineLatest([this.partnerId$, this.pipelineUpdateEvents$$]).pipe(
      switchMap(([pid, _]) => {
        return this.salesPipelinesService.list(pid);
      }),
      map((pipelines) => new Map(pipelines.map((pipeline) => [pipeline.pipelineId, pipeline]))),
    );

    this.currentPipeline$ = combineLatest([
      this.partnerId$,
      this.pipelines$,
      this.currentPipelineId$$,
      this.config.currentPipeline$,
    ]).pipe(
      map(([partnerId, pipelines, pipelineId, defaultPipeline]) => {
        const found = pipelines.get(pipelineId);
        if (!found) {
          return {
            pipelineId: defaultPipeline.id,
            partnerId: partnerId,
            name: defaultPipeline.name,
            stages: defaultPipeline.stages,
          } as PipelineInterface;
        } else {
          return found;
        }
      }),
    );
  }

  createDefaultPipeline(): Observable<boolean> {
    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.salesPipelinesService.createPipeline(partnerId, this.defaultPipelineName, this.defaultPipelineStages),
      ),
      map((resp) => {
        if (resp?.pipelineId) {
          this.currentPipelineId$$.next(resp.pipelineId);
          this.pipelineUpdateEvents$$.next(resp.pipelineId);
          return true;
        } else {
          return false;
        }
      }),
      catchError(() => of(false)),
    );
  }
}
